import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitPullRequest, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ChangeRequestSummaryDto, DocumentDetailDto, DocumentSummaryDto, Paginated, UserSummary } from '@imsenta/shared';
import { PageHeader } from '@/components/AppShell';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  StatusBadge,
  Tabs,
  Textarea,
  useToast,
} from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { fmtRelative } from '@/lib/format';
import { useSession } from '@/lib/session';

type Filter = 'open' | 'mine' | 'review' | 'closed' | 'all';

export function ChangeRequestsPage() {
  const navigate = useNavigate();
  const { session, can } = useSession();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<Filter>((params.get('filter') as Filter) ?? 'open');
  const [createOpen, setCreateOpen] = useState(params.get('new') === '1');

  const query = {
    open: { state: 'open' },
    mine: { state: 'all', mine: 'true' },
    review: { state: 'open', reviewerId: session?.user.id },
    closed: { state: 'closed' },
    all: { state: 'all' },
  }[filter];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['change-requests', filter, session?.user.id],
    queryFn: () => api.get<Paginated<ChangeRequestSummaryDto>>('/change-requests', { ...query, pageSize: 50 }),
  });

  return (
    <>
      <PageHeader
        title="Change requests"
        description="Proposed changes to policy: discuss, approve or decline them here."
        actions={
          can('changeRequest:create') ? (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreateOpen(true)}>
              Propose a change
            </Button>
          ) : null
        }
      />

      <Tabs
        className="mb-4"
        value={filter}
        onChange={(next) => {
          setFilter(next);
          const params2 = new URLSearchParams(params);
          params2.set('filter', next);
          setParams(params2, { replace: true });
        }}
        tabs={[
          { id: 'open' as Filter, label: 'Open' },
          { id: 'review' as Filter, label: 'Awaiting my review' },
          { id: 'mine' as Filter, label: 'Raised by me' },
          { id: 'closed' as Filter, label: 'Closed' },
          { id: 'all' as Filter, label: 'All' },
        ]}
      />

      <Card>
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} title="Could not load change requests" />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<GitPullRequest className="size-10" />}
            title="Nothing here"
            description={
              filter === 'open'
                ? 'There are no open change requests. Propose one from any published document.'
                : 'No change requests match this filter.'
            }
            action={can('changeRequest:create') ? <Button variant="primary" onClick={() => setCreateOpen(true)}>Propose a change</Button> : undefined}
          />
        ) : (
          <ul className="divide-y divide-ink-100">
            {data.items.map((cr) => (
              <li key={cr.id}>
                <Link to={`/change-requests/${cr.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-3.5 hover:bg-ink-50">
                  <Badge tone="neutral">#{cr.number}</Badge>
                  <div className="min-w-0 flex-1 basis-48">
                    <p className="truncate text-[14px] font-medium text-ink-800">{cr.title}</p>
                    <p className="mt-0.5 flex items-center gap-1.5 truncate text-[12px] text-ink-500">
                      {cr.createdBy ? <Avatar name={cr.createdBy.name} email={cr.createdBy.email} size={15} /> : null}
                      {cr.createdBy?.name ?? 'Unknown'} · {cr.documentReference} {cr.documentTitle} · {fmtRelative(cr.createdAt)}
                    </p>
                  </div>
                  {cr.commentCount > 0 ? <span className="text-[12px] text-ink-500">{cr.commentCount} comments</span> : null}
                  <span
                    className={`text-[12px] font-medium ${
                      cr.changesRequested > 0 ? 'text-amber-600' : cr.approvals >= cr.requiredApprovals ? 'text-emerald-600' : 'text-ink-500'
                    }`}
                  >
                    {cr.approvals}/{cr.requiredApprovals} approved
                  </span>
                  <StatusBadge status={cr.state} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <CreateChangeRequestModal
        open={createOpen}
        documentId={params.get('documentId') ?? undefined}
        onClose={() => {
          setCreateOpen(false);
          const next = new URLSearchParams(params);
          next.delete('new');
          next.delete('documentId');
          setParams(next, { replace: true });
        }}
        onCreated={(id) => navigate(`/change-requests/${id}`)}
      />
    </>
  );
}

function CreateChangeRequestModal({
  open,
  documentId,
  onClose,
  onCreated,
}: {
  open: boolean;
  documentId?: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const { session } = useSession();
  const [selectedDoc, setSelectedDoc] = useState(documentId ?? '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reviewerIds, setReviewerIds] = useState<string[]>([]);
  const [submit, setSubmit] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && documentId) setSelectedDoc(documentId);
  }, [open, documentId]);

  const { data: documents } = useQuery({
    queryKey: ['cr-documents'],
    queryFn: () => api.get<Paginated<DocumentSummaryDto>>('/documents', { pageSize: 100 }),
    enabled: open,
  });

  const { data: members } = useQuery({
    queryKey: ['members'],
    queryFn: () => api.get<{ items: (UserSummary & { role: string })[] }>('/tenant/members'),
    enabled: open,
    retry: false,
  });

  const { data: detail } = useQuery({
    queryKey: ['document', selectedDoc],
    queryFn: () => api.get<DocumentDetailDto>(`/documents/${selectedDoc}`),
    enabled: open && Boolean(selectedDoc),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/change-requests', {
        documentId: selectedDoc,
        title: title.trim(),
        description: description || undefined,
        reviewerIds,
        submit,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['change-requests'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      push(submit ? 'Change request opened for review' : 'Change request saved as a draft', 'success');
      onClose();
      onCreated(created.id);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not create the change request'),
  });

  const eligibleReviewers = (members?.items ?? []).filter((m) => m.id !== session?.user.id && m.role !== 'reader');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Propose a change"
      description="The current draft of the document becomes the proposal. You can keep editing it while it is open."
      width="lg"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} disabled={!selectedDoc || !title.trim()} onClick={() => create.mutate()}>
            {submit ? 'Open for review' : 'Save as draft'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}

        <Field label="Document" required>
          <Select value={selectedDoc} onChange={(e) => setSelectedDoc(e.target.value)}>
            <option value="">Choose a document…</option>
            {documents?.items.map((doc) => (
              <option key={doc.id} value={doc.id}>
                {doc.reference} · {doc.title}
              </option>
            ))}
          </Select>
        </Field>

        {detail && !detail.draft && !detail.published ? (
          <Alert tone="warning">That document has no content yet. Add some before proposing a change.</Alert>
        ) : detail?.draft ? (
          <Alert tone="info">The unsaved draft of this document will be used as the proposal.</Alert>
        ) : null}

        <Field label="Title" required hint="A short summary of what you are proposing.">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add post-incident review trigger" />
        </Field>

        <Field label="Rationale" hint="Why is this change needed? Reviewers see this first.">
          <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        {eligibleReviewers.length ? (
          <Field label="Request review from" hint="Optional. They will see it in “Awaiting my review”.">
            <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-lg border border-ink-200 p-2.5">
              {eligibleReviewers.map((member) => (
                <Checkbox
                  key={member.id}
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      {member.name}
                      <span className="text-ink-400 capitalize">· {member.role}</span>
                    </span>
                  }
                  checked={reviewerIds.includes(member.id)}
                  onChange={(e) =>
                    setReviewerIds((current) => (e.target.checked ? [...current, member.id] : current.filter((id) => id !== member.id)))
                  }
                />
              ))}
            </div>
          </Field>
        ) : null}

        <Checkbox
          label="Open for review now"
          hint="Leave unticked to keep working on it privately as a draft."
          checked={submit}
          onChange={(e) => setSubmit(e.target.checked)}
        />
      </div>
    </Modal>
  );
}
